import React, { useCallback, useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import { MMediaImage } from '../MMediaImage';
import { ACarouselArrow, ACarouselArrowDirection } from '../../atoms/ACarouselArrow';
import { MCarouselControls, MCarouselControlsType } from '../MCarouselControls';
import { useDirection } from '../../hooks';
import { Themes } from '../../utils/enums/enums';
import type { MMediaCarouselProps, MMediaCarouselMediaImageItem, MMediaImageMultiItem } from './MMediaCarousel.types';
import { MMediaCarouselCarouselStyle as CarouselStyle } from './MMediaCarousel.types';
import * as Styled from './MMediaCarousel.styles';
import 'swiper/css';

const COMPONENT_NAME = 'm-web-ui-kit-MMediaCarousel';
const TEST_ID = 'web-ui-kit-MMediaCarousel';

export const MMediaCarousel = ({
  carouselStyle = CarouselStyle.Standard,
  carouselType,
  mCarouselControls,
  hover = false,
  logo = false,
  mMediaImage = [],
  mMediaImageMulti = [],
  renditionSpecs,
  slideRenditionSpecsResolver,
  slideClassNameResolver,
  aCarouselArrow,
  className,
  ariaLabel = 'Image carousel',
  onKeyDown,
  ...rest
}: MMediaCarouselProps): React.ReactElement | null => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [slideAnnouncement, setSlideAnnouncement] = useState('');
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const { direction } = useDirection();

  const useLegacySlides = mMediaImage.length === 0 && mMediaImageMulti.length > 0;
  const slides: (MMediaCarouselMediaImageItem | MMediaImageMultiItem)[] = useLegacySlides
    ? mMediaImageMulti
    : mMediaImage;
  const totalSlides = slides.length;
  const showControls = totalSlides > 1;

  const handleNext = useCallback(() => {
    if (swiperInstance) {
      swiperInstance.slideNext();
    }
  }, [swiperInstance]);

  const handlePrev = useCallback(() => {
    if (swiperInstance) {
      swiperInstance.slidePrev();
    }
  }, [swiperInstance]);

  const handleSlideChange = useCallback(
    (swiper: SwiperType) => {
      const nextSlide = swiper.realIndex;

      setCurrentSlide(nextSlide);
      setSlideAnnouncement(`Slide ${nextSlide + 1} of ${totalSlides}`);
    },
    [totalSlides]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      onKeyDown?.(event);

      if (event.defaultPrevented || !showControls) {
        return;
      }

      const target = event.target as HTMLElement;
      const isNavigationArrow =
        target.closest('[data-testid="web-ui-kit-ACarouselArrow"]') ||
        target.closest('[data-testid="carousel-arrow-left"]') ||
        target.closest('[data-testid="carousel-arrow-right"]');

      if (!isNavigationArrow) {
        return;
      }

      if (event.key === 'ArrowRight') {
        event.preventDefault();
        handleNext();
      }

      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        handlePrev();
      }
    },
    [handleNext, handlePrev, onKeyDown, showControls]
  );

  if (totalSlides === 0) {
    return null;
  }

  const imageOverlay = !logo;
  const isStandard = carouselStyle === CarouselStyle.Standard;
  const controlsType = carouselType ?? mCarouselControls?.type ?? MCarouselControlsType.Dots;

  const THEME_TOKENS = Object.values(Themes) as string[];
  const classTokens = Array.from(new Set(className?.split(/\s+/).filter(Boolean) ?? []));
  if (!classTokens.some(token => THEME_TOKENS.includes(token))) {
    classTokens.push(Themes.Inverse);
  }
  const wrapperClassName = classTokens.join(' ') || undefined;

  return (
    <Styled.Wrapper
      className={wrapperClassName}
      role="region"
      aria-roledescription="carousel"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      data-component-name={COMPONENT_NAME}
      data-testid={TEST_ID}
      {...rest}
    >
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true" data-testid="carousel-live-region">
        {slideAnnouncement}
      </div>
      <Styled.CarouselContainer>
        <Swiper
          key={direction}
          dir={direction}
          spaceBetween={0}
          slidesPerView={1}
          onSwiper={setSwiperInstance}
          onSlideChange={handleSlideChange}
          loop={showControls}
        >
          {slides.map((item, index) => {
            const slideClassName = slideClassNameResolver?.(item, index);
            const slideRenditionSpecs = slideRenditionSpecsResolver?.(item, index) ?? renditionSpecs;

            return (
              <SwiperSlide
                key={index}
                role="group"
                aria-roledescription="slide"
                aria-label={`Slide ${index + 1} of ${totalSlides}`}
              >
                <Styled.SlideContent className={slideClassName}>
                  {useLegacySlides ? (
                    <MMediaImage
                      item={
                        logo
                          ? (item as MMediaImageMultiItem)
                          : { ...(item as MMediaImageMultiItem), mLogoContainer: undefined }
                      }
                      renditionSpecs={slideRenditionSpecs}
                    />
                  ) : (
                    <MMediaImage
                      aImage={(item as MMediaCarouselMediaImageItem).aImage}
                      mButtonIcon={(item as MMediaCarouselMediaImageItem).mButtonIcon}
                      aScrimGradient={logo ? (item as MMediaCarouselMediaImageItem).aScrimGradient : undefined}
                      mTag={(item as MMediaCarouselMediaImageItem).mTag}
                      mLogoContainer={logo ? (item as MMediaCarouselMediaImageItem).mLogoContainer : undefined}
                      renditionSpecs={slideRenditionSpecs}
                    />
                  )}
                </Styled.SlideContent>
              </SwiperSlide>
            );
          })}
        </Swiper>

        {showControls && (
          <>
            {isStandard && (
              <Styled.ArrowsWrapper $revealOnHover={hover}>
                <Styled.ArrowLeft>
                  <ACarouselArrow
                    direction={ACarouselArrowDirection.Left}
                    disabled={aCarouselArrow?.disabled === true || aCarouselArrow?.disabled === 'true'}
                    transparent={aCarouselArrow?.transparent === true || aCarouselArrow?.transparent === 'true'}
                    onClick={handlePrev}
                  />
                </Styled.ArrowLeft>
                <Styled.ArrowRight>
                  <ACarouselArrow
                    direction={ACarouselArrowDirection.Right}
                    disabled={aCarouselArrow?.disabled === true || aCarouselArrow?.disabled === 'true'}
                    transparent={aCarouselArrow?.transparent === true || aCarouselArrow?.transparent === 'true'}
                    onClick={handleNext}
                  />
                </Styled.ArrowRight>
              </Styled.ArrowsWrapper>
            )}

            <Styled.ControlsWrapper>
              <MCarouselControls
                type={controlsType}
                imageOverlay={imageOverlay}
                arrows={!isStandard}
                loop={showControls}
                totalSlides={totalSlides}
                currentSlide={currentSlide}
                onNext={handleNext}
                onPrev={handlePrev}
              />
            </Styled.ControlsWrapper>
          </>
        )}
      </Styled.CarouselContainer>
    </Styled.Wrapper>
  );
};

export { MMediaCarouselCarouselStyle } from './MMediaCarousel.types';
export type { MMediaCarouselCarouselControls, MMediaCarouselProps, MMediaImageMultiItem } from './MMediaCarousel.types';
